import type {
    PropertyGroupPayload,
    PropertyGroupSibling,
} from "../types/userListing";

// Same split-on-capitals formatting ListingHeader and the MLS review dialogs use.
const formatPropertyType = (value: string): string =>
    value.replace(/([A-Z])/g, " $1").replace(/,/g, ", ").trim();

export const propertyGroupBadgeLabel = (p: PropertyGroupPayload): string =>
    p.marketSeparately
        ? "Marketed separately"
        : p.kind === "salerent"
        ? "Sale + rental"
        : "Cross-listed";

export const propertyGroupWhy = (p: PropertyGroupPayload): string =>
    p.kind === "salerent"
        ? "Same property listed for sale and for rent."
        : "Same property cross-listed under more than one property type.";

// A cross-listed group markets exactly one record: the sibling the api marked
// primary (first sibling as a fallback for a payload without the marker).
const crosslistPrimaryName = (p: PropertyGroupPayload): string => {
    const primary = p.siblings.find((s) => s.isPrimary) ?? p.siblings[0];
    return primary?.mlsId ? `MLS# ${primary.mlsId}` : "the other record";
};

// Present tense only once the account's flag is a confirmed true (DESIGN.md §2:
// manual campaigns are untouched). A sale+rent pair shares the posts; a
// cross-listing's primary carries them while the other record is held (D2).
export const propertyGroupEffect = (
    p: PropertyGroupPayload,
    autopilotEnabled?: boolean | null,
): string => {
    if (autopilotEnabled === true) {
        if (p.marketSeparately) {
            return "Marketed on its own: its own Auto Pilot listing and its own weekly posts.";
        }
        if (p.kind === "salerent") {
            return "Counts as one Auto Pilot listing; the records share its weekly posts.";
        }
        return p.isPrimary
            ? "Counts as one Auto Pilot listing; this record carries its weekly posts."
            : `Counts as one Auto Pilot listing; ${crosslistPrimaryName(p)} carries its weekly posts and this record's campaign stays paused.`;
    }
    if (p.marketSeparately) {
        return "Under Auto Pilot it is marketed on its own, with its own listing and its own weekly posts.";
    }
    if (p.kind === "salerent") {
        return "Under Auto Pilot the records count as one listing and share its weekly posts.";
    }
    return p.isPrimary
        ? "Under Auto Pilot the records count as one listing and this record would carry its weekly posts."
        : `Under Auto Pilot the records count as one listing; ${crosslistPrimaryName(p)} would carry its weekly posts and this record's campaign would stay paused.`;
};

export const propertyGroupTooltip = (
    p: PropertyGroupPayload,
    autopilotEnabled?: boolean | null,
): string =>
    `${propertyGroupWhy(p)} ${propertyGroupEffect(p, autopilotEnabled)}`;

export const propertyGroupSiblingLine = (s: PropertyGroupSibling): string =>
    [
        s.mlsId ? `MLS# ${s.mlsId}` : "MLS record",
        s.propertyType ? formatPropertyType(s.propertyType) : null,
        s.saleType === "rent" ? "For rent" : "For sale",
        s.price ? `$${s.price.toLocaleString()}` : null,
    ]
        .filter(Boolean)
        .join(" · ");
